/**
 * EmailSmtpSettingsPage — SMTP beállítások admin UI
 * Story 2.3: SMTP beállítások admin UI és kapcsolat teszt
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@kgc/i18n';
import { toast } from '@kgc/ui';
import { useState } from 'react';

export function EmailSmtpSettingsPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    smtpHost: '',
    smtpPort: '587',
    smtpUser: '',
    smtpPass: '',
    senderEmail: '',
    senderName: '',
    dailyLimit: '500',
  });
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  function handleChange(field: keyof typeof form, value: string) {
    setForm(prev => ({ ...prev, [field]: value }));
    setTestResult(null);
  }

  async function handleTestConnection() {
    setTesting(true);
    setTestResult(null);
    try {
      // TODO: wire to POST /settings/email-config/test when endpoint exists
      await new Promise(r => setTimeout(r, 1000));
      setTestResult({ ok: true, message: t('email.connectionOk') });
    } catch {
      setTestResult({ ok: false, message: t('email.connectionFailed') });
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      // TODO: wire to PUT /settings/email-config when endpoint exists
      await new Promise(r => setTimeout(r, 500));
      toast.success(t('email.saveSuccess'));
    } catch {
      toast.error(t('common.errorLabel'));
    } finally {
      setSaving(false);
    }
  }

  const fields: Array<{ key: keyof typeof form; label: string; type?: string | undefined }> = [
    { key: 'smtpHost', label: t('email.smtpHost') },
    { key: 'smtpPort', label: t('email.smtpPort'), type: 'number' },
    { key: 'smtpUser', label: t('email.smtpUser') },
    { key: 'smtpPass', label: t('email.smtpPass'), type: 'password' },
    { key: 'senderEmail', label: t('email.senderEmail'), type: 'email' },
    { key: 'senderName', label: t('email.senderName') },
    { key: 'dailyLimit', label: t('email.dailyLimit'), type: 'number' },
  ];

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">{t('email.smtpSettings')}</h1>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('email.smtpSettings')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {fields.map(f => (
            <div key={f.key}>
              <label className="text-sm font-medium text-foreground">{f.label}</label>
              <Input
                type={f.type ?? 'text'}
                value={form[f.key]}
                onChange={e => handleChange(f.key, e.target.value)}
                placeholder={f.key === 'smtpPass' ? '••••••' : undefined}
                className="mt-1"
              />
            </div>
          ))}

          {/* Test result */}
          {testResult && (
            <div className={`text-sm ${testResult.ok ? 'text-green-600' : 'text-destructive'}`}>
              {testResult.message}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t">
            <Button variant="outline" onClick={handleTestConnection} disabled={testing}>
              {testing ? t('common.loading') : t('email.testConnection')}
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? t('email.saving') : t('email.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
